/**
 * Read status for the channel / DM thread.
 *
 *  - useMarkChannelRead: while a channel is open AND the page is visible and
 *    focused, tell the server everything up to the newest message is read —
 *    debounced, once per newest message, again when new messages arrive while
 *    the user is looking, or when they come back to the tab.
 *  - SeenStatus: the line under the caller's own messages. Group channels say
 *    "Seen by N of M"; DMs say "Seen 2:14 PM" or "Delivered". Clicking it opens
 *    who has / hasn't read the message (the read-receipts endpoint is limited to
 *    the sender and channel owners/admins; a 403 reads as a plain notice).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, CheckCheck, Loader2 } from 'lucide-react';
import { messagingApi, Message } from '@/services/messagingApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { formatClockTime, parseServerDate } from './scheduleSend';

export const MARK_READ_DEBOUNCE_MS = 800;

function pageIsVisible(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.visibilityState && document.visibilityState !== 'visible') return false;
  return typeof document.hasFocus === 'function' ? document.hasFocus() : true;
}

/**
 * Bulk-mark a channel read up to `newestMessageId` while it is on screen.
 * `onMarked` runs after a successful call (refresh unread badges).
 */
export function useMarkChannelRead(
  channelId: number,
  newestMessageId: number | null,
  { enabled = true, onMarked, delayMs = MARK_READ_DEBOUNCE_MS }: { enabled?: boolean; onMarked?: () => void; delayMs?: number } = {},
) {
  // Newest id the server has already been told about, per channel.
  const sentRef = useRef<Map<number, number>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ channelId, newestMessageId, enabled, onMarked });
  latest.current = { channelId, newestMessageId, enabled, onMarked };

  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      const { channelId: cid, newestMessageId: upTo, enabled: on, onMarked: done } = latest.current;
      if (!on || upTo == null || !pageIsVisible()) return;
      if ((sentRef.current.get(cid) ?? -1) >= upTo) return;
      sentRef.current.set(cid, upTo);
      try {
        await messagingApi.markChannelRead(cid, upTo);
        done?.();
      } catch {
        // Not fatal (older backend, offline): forget it so the next trigger retries.
        if (sentRef.current.get(cid) === upTo) sentRef.current.delete(cid);
      }
    }, delayMs);
  }, [delayMs]);

  useEffect(() => {
    if (enabled && newestMessageId != null) schedule();
  }, [channelId, newestMessageId, enabled, schedule]);

  useEffect(() => {
    const onVisible = () => {
      if (pageIsVisible()) schedule();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [schedule]);
}

/** Newest message id in a list (ids grow with time), or null for an empty list. */
export function newestMessageId(messages: Pick<Message, 'id'>[]): number | null {
  let max: number | null = null;
  for (const m of messages) if (typeof m.id === 'number' && (max == null || m.id > max)) max = m.id;
  return max;
}

function ReceiptsBody({ messageId }: { messageId: number }) {
  const { t, language } = useAppTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['read-receipts', messageId],
    queryFn: () => messagingApi.getReadReceipts(messageId),
    retry: false,
    staleTime: 10_000,
  });
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-1 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('messaging.receipts.loading', 'Loading…')}
      </div>
    );
  }
  if (error) {
    const status = (error as { status?: number }).status;
    return (
      <p className="p-1 text-sm text-muted-foreground" data-testid="receipts-error">
        {status === 403
          ? t('messaging.receipts.forbidden', 'Only the sender and channel admins can see who read this message.')
          : t('messaging.receipts.failed', 'Could not load read receipts.')}
      </p>
    );
  }
  const read = data?.read ?? [];
  const unread = data?.unread ?? [];
  return (
    <div className="space-y-3 text-sm" data-testid="receipts-popover">
      <section>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('messaging.receipts.readHeading', 'Read ({{count}})', { count: read.length })}
        </h4>
        {read.length === 0 ? (
          <p className="text-muted-foreground">{t('messaging.receipts.noneRead', 'No one yet')}</p>
        ) : (
          <ul className="space-y-1">
            {read.map((r) => {
              const at = parseServerDate(r.read_at ?? null);
              return (
                <li key={`r-${r.user_id ?? 'm'}-${r.member_id ?? ''}-${r.name}`} className="flex items-center justify-between gap-3">
                  <span className="truncate">{r.name}</span>
                  {at && <span className="flex-shrink-0 text-xs text-muted-foreground">{formatClockTime(at, language)}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <section>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('messaging.receipts.unreadHeading', 'Not read yet ({{count}})', { count: unread.length })}
        </h4>
        {unread.length === 0 ? (
          <p className="text-muted-foreground">{t('messaging.receipts.allRead', 'Everyone has read it')}</p>
        ) : (
          <ul className="space-y-1">
            {unread.map((r) => (
              <li key={`u-${r.user_id ?? 'm'}-${r.member_id ?? ''}-${r.name}`} className="truncate text-muted-foreground">
                {r.name}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

interface SeenStatusProps {
  message: Message;
  isDM: boolean;
  /** The caller's newest message in the thread: always shown, others on hover. */
  isLatestOwn: boolean;
  visible: boolean;
  align?: 'left' | 'right';
}

/** "Seen by N of M" / "Seen 2:14 PM" / "Delivered" under one of the caller's messages. */
export function SeenStatus({ message, isDM, isLatestOwn, visible, align = 'right' }: SeenStatusProps) {
  const { t, language } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const recipients = message.recipient_count;
  const readBy = message.read_by_count ?? 0;
  if (recipients == null && !(isDM && isLatestOwn)) return null;
  if (!visible && !isLatestOwn && !open) return null;

  let label: string;
  let seen: boolean;
  if (isDM) {
    const seenAt = isLatestOwn ? parseServerDate(message.seen_at ?? null) : null;
    seen = !!seenAt || readBy > 0;
    label = seenAt
      ? t('messaging.receipts.seenAt', 'Seen {{time}}', { time: formatClockTime(seenAt, language) })
      : seen
        ? t('messaging.receipts.seen', 'Seen')
        : t('messaging.receipts.delivered', 'Delivered');
  } else {
    if (!recipients) return null;
    seen = readBy > 0;
    label = t('messaging.receipts.seenBy', 'Seen by {{count}} of {{total}}', { count: readBy, total: recipients });
  }
  const Icon = seen ? CheckCheck : Check;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            'mt-0.5 inline-flex items-center gap-1 rounded px-1 text-[11px] text-muted-foreground hover:text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
            seen && 'text-blue-600 dark:text-blue-400',
            align === 'right' ? 'self-end' : 'self-start',
          )}
          data-testid="seen-status"
          aria-label={t('messaging.receipts.open', '{{label}} — show who has read this', { label })}
        >
          <Icon className="h-3 w-3" />
          {label}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          align={align === 'right' ? 'end' : 'start'}
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-h-80 w-64 overflow-y-auto rounded-md border bg-popover p-3 text-popover-foreground shadow-md"
        >
          {open && <ReceiptsBody messageId={message.id} />}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export default SeenStatus;
